import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgSelectComponent } from "@ng-select/ng-select";
import { AlertController, ModalController } from "ionic-angular";
import { from } from "rxjs/observable/from";
import { of } from "rxjs/observable/of";
import { catchError, map, mergeMap, toArray } from "rxjs/operators";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "billing-codes",
  templateUrl: "billing-codes.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingCodes),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BillingCodes),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BillingCodes implements OnInit, OnDestroy {
  @ViewChild("otherPeriodsDropDown") otherPeriodsDropDown: NgSelectComponent;

  @Input() currentPayer: any;
  @Input() currentPayerType: any;
  @Input() currentBenefitType: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onSetCheckmark = new EventEmitter<any>();

  tabs = [
    {
      id: "hospice-loc",
      title: "Hospice LOC",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
    },
    {
      id: "hospice-visits",
      title: "Hospice Visits",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
    },
    {
      id: "palliative",
      title: "Palliative",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
    },
    {
      id: "room-and-board",
      title: "Room and Board",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
    },
  ];
  activeTab = {};
  activeTabId = "";

  payerType: string;
  benefitType: string[];

  constructor(
    protected alertController: AlertController,
    public modalCtrl: ModalController,
    private payersService: PayersService
  ) {}

  ngOnInit(): void {
    this.tabSelection();
    if (this.currentBenefitType)
      this.validateCheckmarkInfo().then((result) => {
        if (result) this.onSetCheckmark.emit();
      });
  }

  tabSelection() {
    if (this.currentPayer) {
      this.payerType = this.currentPayer.payerType;

      if (this.currentPayer.benefitType) {
        this.benefitType = this.currentPayer.benefitType.split(",");
        this.benefitType.forEach((type) => {
          if (type.toLowerCase().trim() == "room and board") {
            this.tabs[3].visible = true;
          } else if (type.toLowerCase().trim() == "palliative") {
            this.tabs[2].visible = true;
          } else if (
            type.toLowerCase() == "hospice" &&
            this.payerType.toUpperCase() != "MEDICARE"
          ) {
            this.tabs[0].visible = true;
            this.tabs[1].visible = true;
          }
        });
      }
    }
    for (let i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].visible) {
        this.activeTab = this.tabs[i];
        this.activeTabId = this.tabs[i].id;
        i = this.tabs.length + 1;
      }
    }
  }

  ngOnChanges() {
    this.payerType = this.currentPayerType;
    this.benefitType = this.currentBenefitType;
    this.tabSelection();
    if (
      this.benefitType &&
      this.benefitType.length > 0 &&
      this.payerType &&
      this.payerType.length > 0
    ) {
      this.checkTabVisibility();
    }
  }

  getVisibleTabs() {
    return this.tabs.filter((tab) => tab.visible);
  }

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  protected readonly event = event;

  public cancel(): void {
    this.onCanceled.emit(true);
  }

  ngOnDestroy(): void {}

  checkTabVisibility() {
    this.tabs = this.tabs.map((tab) => {
      switch (tab.id) {
        case "hospice-loc":
        case "hospice-visits":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            this.currentBenefitType.includes("HOSPICE");
          break;
        case "room-and-board":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            (this.currentBenefitType.includes("ROOM AND BOARD") ||
              this.currentBenefitType.includes("ROOMANDBOARD"));
          break;
        default:
          this.activeTab = "";
          this.activeTabId = "";
          break;
      }
      return tab;
    });
  }

  toPascalCase(s: string) {
    return s.replace(
      /\w\S*/g,
      (m) => m.charAt(0).toUpperCase() + m.substr(1).toLowerCase()
    );
  }

  private validateCheckmarkInfo() {
    let benefitTypes = this.currentBenefitType.split(",");

    return new Promise((resolve, reject) => {
      const apiCalls = [];
      for (let benefit of benefitTypes) {
        if (benefit === "HOSPICE") {
          apiCalls.push(
            this.payersService.getPayerBenefitCodesPeriods(
              this.currentPayer.payerId,
              "HOSPICE",
              "LOC"
            )
          );
          apiCalls.push(
            this.payersService.getPayerBenefitCodesPeriods(
              this.currentPayer.payerId,
              "HOSPICE",
              "VISITS"
            )
          );
        } else if (
          benefit.toUpperCase().replaceAll(" ", "") === "ROOMANDBOARD"
        ) {
          apiCalls.push(
            this.payersService.getPayerBenefitCodesPeriods(
              this.currentPayer.payerId,
              "ROOMANDBOARD",
              null
            )
          );
        }
      }
      from(apiCalls)
        .pipe(
          mergeMap((apiCall, index) => {
            return apiCall.pipe(
              catchError((error) => {
                return of(null);
              })
            );
          }),
          toArray(),
          map((results) => {
            // @ts-ignore
            const hasValidResult = results.some(
              (result) =>
                // @ts-ignore
                result !== null && result.length > 0
            );

            resolve(hasValidResult);
            return hasValidResult;
          })
        )
        .subscribe({
          error: (error) => reject(error),
          complete: () => {},
        });
    });
  }

  setCheckMark() {
    this.onSetCheckmark.emit();
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/billing-codes/billing-codes.ts